import { useEffect } from "react";
import { AppHeader } from "@/components/app-header";
import { AppFooter } from "@/components/app-footer";

export default function PrivacyPage() {
  useEffect(() => { document.title = "Privacy Policy | DanceMirror"; window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <AppHeader />
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12 space-y-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold" data-testid="text-privacy-title">Privacy Policy</h1>
            <p className="text-sm text-muted-foreground mt-2" data-testid="text-privacy-effective">Effective Date: February 11, 2026</p>
          </div>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">1. Introduction</h2>
            <p className="text-muted-foreground leading-relaxed">
              DanceMirror ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you use our web application. By using DanceMirror, you consent to the practices described in this policy.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">2. Information We Collect</h2>
            <div className="space-y-3">
              <div>
                <h3 className="text-sm font-medium">Account Information</h3>
                <p className="text-muted-foreground leading-relaxed">
                  When you sign in with Google, we receive your name, email address, and profile picture from your Google account. We use this information to create and manage your DanceMirror account.
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium">Payment Information</h3>
                <p className="text-muted-foreground leading-relaxed">
                  If you subscribe to a paid plan, your payment information is collected and processed directly by Stripe. We do not store your credit card numbers or other sensitive payment details on our servers. We retain your Stripe customer ID and subscription status to manage your account.
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium">Uploaded Content</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Videos you upload are stored and delivered via Cloudflare Stream. We store metadata about your saved videos (titles, settings, video references) in our database to provide the library feature.
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium">Usage Data</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We may collect basic usage information such as pages visited, features used, and video settings applied to improve the Service.
                </p>
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">3. How We Use Your Information</h2>
            <p className="text-muted-foreground leading-relaxed">
              We use the information we collect to: provide and maintain the Service; manage your account and subscription; store and deliver your uploaded videos; process payments through Stripe; improve and optimize the Service; communicate with you about your account or the Service; and comply with legal obligations.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">4. Third-Party Services</h2>
            <div className="space-y-3">
              <div>
                <h3 className="text-sm font-medium">Google OAuth</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We use Google OAuth 2.0 for authentication. When you sign in, Google shares your basic profile information with us as authorized by you. Please refer to Google's Privacy Policy for details on how Google handles your data.
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium">Stripe</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Payment processing is handled by Stripe. Stripe collects and processes your payment information in accordance with their Privacy Policy and PCI compliance standards.
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium">Cloudflare Stream</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Uploaded videos are stored and delivered through Cloudflare Stream. Cloudflare may process data in accordance with their Privacy Policy.
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium">YouTube</h3>
                <p className="text-muted-foreground leading-relaxed">
                  When you watch YouTube videos through the Service, YouTube's embedded player may collect data according to YouTube's Privacy Policy and Google's Privacy Policy. DanceMirror does not control the data YouTube collects through its embedded player.
                </p>
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">5. Cookies and Sessions</h2>
            <p className="text-muted-foreground leading-relaxed">
              We use session cookies to maintain your login state. These cookies are essential for the Service to function and are not used for tracking or advertising purposes. YouTube's embedded player may set its own cookies when you view YouTube content within the Service.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">6. Data Retention</h2>
            <p className="text-muted-foreground leading-relaxed">
              We retain your account information and saved video data for as long as your account is active. Uploaded video files stored on Cloudflare Stream are retained until you delete them from your library or your account is terminated. You may request deletion of your account and associated data at any time by contacting us.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">7. Data Security</h2>
            <p className="text-muted-foreground leading-relaxed">
              We implement appropriate technical and organizational measures to protect your personal information. All data transmission is encrypted via HTTPS. However, no method of electronic storage or transmission is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">8. Your Rights</h2>
            <p className="text-muted-foreground leading-relaxed">
              Depending on your location, you may have the right to: access the personal information we hold about you; request correction of inaccurate information; request deletion of your data; object to the processing of your data; and export your data in a portable format. To exercise any of these rights, please contact us at the email address below.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">9. Children's Privacy</h2>
            <p className="text-muted-foreground leading-relaxed">
              DanceMirror is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe a child under 13 has provided us with personal information, please contact us so we can take appropriate action.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">10. Changes to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy on this page with a revised effective date. Your continued use of the Service after any changes indicates your acceptance of the updated policy.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">11. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have questions or concerns about this Privacy Policy or our data practices, please contact us at{" "}
              <a href="mailto:support@payong.io?subject=DanceMirror%20Support" className="text-primary hover:underline" data-testid="link-privacy-contact">
                support@payong.io
              </a>.
            </p>
          </section>
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
